import {
  Button,
  Collapse,
  Icons,
  Input,
  List,
  Popconfirm,
  Select,
  Space,
  Steps,
  Table,
  Tag,
  TextField,
  Tooltip,
  useModalForm,
} from '@pankod/refine-antd'
import {
  IResourceComponentsProps,
  GetListResponse,
  CrudFilters,
  LogicalFilter,
  useCustomMutation,
  useGetIdentity,
  useInvalidate,
} from '@pankod/refine-core'
import Head from 'next/head'
import React from 'react'

import Filter from '@components/ContentFilter'
import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import { TCommonError } from 'src/interfaces/common'
import {
  TCertificateCategory,
  TCertificateCategoryActor,
  TCertificateCategoryRequest,
  TFilterForm,
} from './types'
import CertificateCategoryModalForm from './blocks/CertificateCategoryModalForm'
import useTableWithMeta from 'src/hooks/useTableWithMeta'

const CERTIFICATE_CATEGORIES_RESOURCE = 'certificate-categories'

const getActorLabel = (actor: TCertificateCategoryActor) =>
  actor.name || actor.email || actor.userId

const getActorDescription = (actor: TCertificateCategoryActor) => (
  <div className="text-xs leading-5">
    <div>
      <strong>Nama:</strong> {actor.name || '-'}
    </div>
    <div>
      <strong>Email:</strong> {actor.email || '-'}
    </div>
    <div>
      <strong>Jabatan:</strong> {actor.jabatan || '-'}
    </div>
    <div>
      <strong>Unit Kerja:</strong> {actor.unitKerja || '-'}
    </div>
    <div>
      <strong>Role:</strong> {actor.role === 'approver' ? 'Approver' : 'Signer'}
    </div>
  </div>
)

const getDefaultEditValues = (
  record: TCertificateCategory,
): TCertificateCategoryRequest => ({
  name: record.name,
  isActive: record.isActive,
  actors: [...(record.actors || [])]
    .sort((left, right) => left.order - right.order)
    .map(({ userId, role, order, jabatan, unitKerja }) => ({
      userId,
      role,
      order,
      jabatan,
      unitKerja,
    })),
})

export const LMSCertificateCategoriesManagementList: React.FC<
  IResourceComponentsProps<GetListResponse<TCertificateCategory>>
> = ({ options }) => {
  const [selectedEditRecord, setSelectedEditRecord] =
    React.useState<TCertificateCategory | null>(null)
  const { isSuccess } = useGetIdentity()
  const invalidate = useInvalidate()
  const { mutateAsync: toggleStatus, isLoading: isTogglingStatus } =
    useCustomMutation()

  const {
    modalProps: createModalProps,
    formProps: createFormProps,
    show: createShow,
    close: createClose,
  } = useModalForm<
    TCertificateCategory,
    TCommonError,
    TCertificateCategoryRequest
  >({
    action: 'create',
    dataProviderName: 'lms',
    resource: CERTIFICATE_CATEGORIES_RESOURCE,
    redirect: false,
    successNotification: {
      message: 'Kategori sertifikat berhasil dibuat',
      type: 'success',
      description: 'Sukses',
    },
    errorNotification: (error) =>
      showErrorNotification(
        error,
        'Terdapat gangguan saat membuat kategori sertifikat',
      ),
  })

  const {
    modalProps: editModalProps,
    formProps: editFormProps,
    show: editShow,
    close: editClose,
  } = useModalForm<
    TCertificateCategory,
    TCommonError,
    TCertificateCategoryRequest
  >({
    action: 'edit',
    dataProviderName: 'lms',
    resource: CERTIFICATE_CATEGORIES_RESOURCE,
    redirect: false,
    queryOptions: {
      enabled: false,
    },
    successNotification: {
      message: 'Kategori sertifikat berhasil diubah',
      type: 'success',
      description: 'Sukses',
    },
    errorNotification: (error) =>
      showErrorNotification(
        error,
        'Terdapat gangguan saat mengubah kategori sertifikat',
      ),
  })

  const { tableProps, searchFormProps, filters } = useTableWithMeta<
    TCertificateCategory,
    TCommonError,
    TFilterForm
  >({
    syncWithLocation: true,
    queryOptions: {
      enabled: isSuccess,
    },
    resource: CERTIFICATE_CATEGORIES_RESOURCE,
    dataProviderName: 'lms',
    onSearch: (params) => {
      const filters: CrudFilters = []
      const { name, isActive } = params

      filters.push(
        {
          field: 'name',
          operator: 'eq',
          value: name,
        },
        {
          field: 'isActive',
          operator: 'eq',
          value: isActive,
        },
      )

      return filters
    },
    metaData: {
      queryArrayFormat: 'query',
    },
    defaultSetFilterBehavior: 'merge',
  })

  React.useEffect(() => {
    if (!editModalProps.visible || !selectedEditRecord) {
      return
    }

    editFormProps.form?.setFieldsValue(getDefaultEditValues(selectedEditRecord))
  }, [editModalProps.visible, selectedEditRecord, editFormProps.form])

  return (
    <>
      <List
        title="Daftar Kategori Sertifikat"
        createButtonProps={{
          children: 'Tambah Kategori',
          onClick: () => createShow(),
          type: 'primary',
          icon: <Icons.PlusOutlined />,
        }}
      >
        <Head>
          <title>LMS Admin | {options?.label}</title>
        </Head>

        <Collapse className="!mb-4" bordered={false}>
          <Collapse.Panel
            header={
              <>
                {filters?.length ? (
                  <Icons.FilterFilled
                    className="mr-1"
                    style={{ color: '#08c' }}
                  />
                ) : (
                  <Icons.FilterOutlined className="mr-1" />
                )}
                <TextField
                  value="Filter Kategori"
                  strong={Boolean(filters?.length)}
                />
              </>
            }
            key={'filter-categories'}
          >
            <Filter
              layout="vertical"
              formProps={searchFormProps}
              filters={filters as LogicalFilter[]}
              items={[
                {
                  label: 'Nama Kategori',
                  name: 'name',
                  component: (
                    <Input
                      placeholder="Cari nama kategori"
                      autoComplete="off"
                      allowClear
                    />
                  ),
                },
                {
                  label: 'Status',
                  name: 'isActive',
                  component: (
                    <Select
                      options={[
                        { label: 'Aktif', value: true },
                        { label: 'Tidak Aktif', value: false },
                      ]}
                      placeholder="Filter status"
                      allowClear
                    />
                  ),
                },
              ]}
            />
          </Collapse.Panel>
        </Collapse>

        <Table
          {...tableProps}
          bordered
          rowKey="id"
          scroll={{ x: 800 }}
          tableLayout="fixed"
          size="small"
          expandable={{
            rowExpandable: (record) => Boolean(record.actors?.length),
            expandedRowRender: (record) => {
              const sortedActors = [...(record.actors || [])].sort(
                (left, right) => left.order - right.order,
              )

              return (
                <div className="py-2">
                  <Steps direction="vertical" size="small" current={-1}>
                    {sortedActors.map((actor) => (
                      <Steps.Step
                        key={`${record.id}-${actor.userId}-${actor.order}`}
                        title={getActorLabel(actor)}
                        description={getActorDescription(actor)}
                      />
                    ))}
                  </Steps>
                </div>
              )
            },
          }}
        >
          <Table.Column
            dataIndex="id"
            title="ID"
            render={(value) => <TextField value={value} />}
            width={100}
          />
          <Table.Column
            dataIndex="name"
            title="Nama Kategori"
            render={(value) => <TextField value={value} />}
            width={250}
          />
          <Table.Column
            dataIndex="actors"
            title="Approvers / Signers"
            width={150}
            render={(actors: TCertificateCategoryActor[]) => {
              if (!actors?.length) {
                return <TextField value="—" />
              }

              const sortedActors = [...actors].sort(
                (left, right) => left.order - right.order,
              )
              const approverCount = actors.filter(
                (a) => a.role === 'approver',
              ).length
              const signerCount = actors.filter(
                (a) => a.role === 'signer',
              ).length
              const flow = sortedActors.map(getActorLabel).join(' → ')

              return (
                <Tooltip title={`Flow: ${flow}`}>
                  <TextField
                    value={`${approverCount} Approvers / ${signerCount} Signers`}
                  />
                </Tooltip>
              )
            }}
          />
          <Table.Column
            dataIndex="isActive"
            title="Status"
            width={120}
            render={(value: boolean) => (
              <Tag color={value ? 'green' : 'red'}>
                {value ? 'Aktif' : 'Tidak Aktif'}
              </Tag>
            )}
          />
          <Table.Column<TCertificateCategory>
            title="Aksi"
            width={170}
            fixed="right"
            render={(_, record) => (
              <Space direction="vertical" size="small">
                <Button
                  size="small"
                  icon={<Icons.EditOutlined />}
                  onClick={() => {
                    setSelectedEditRecord(record)
                    editShow(record.id)
                  }}
                  block
                  disabled={isTogglingStatus}
                >
                  Ubah Kategori
                </Button>
                <Popconfirm
                  title={`Apakah Anda yakin ingin ${
                    record.isActive ? 'menonaktifkan' : 'mengaktifkan'
                  } kategori ini?`}
                  okText={record.isActive ? 'Nonaktifkan' : 'Aktifkan'}
                  cancelText="Batal"
                  placement="topRight"
                  onConfirm={() =>
                    toggleStatus({
                      dataProviderName: 'lms',
                      method: 'post',
                      url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/certificate-categories/${record.id}/toggle-active`,
                      values: { isActive: !record.isActive },
                      successNotification: {
                        message: `Kategori sertifikat berhasil ${
                          record.isActive ? 'dinonaktifkan' : 'diaktifkan'
                        }`,
                        type: 'success',
                        description: 'Sukses',
                      },
                      errorNotification: (error) =>
                        showErrorNotification(
                          error,
                          `Terdapat gangguan saat ${
                            record.isActive ? 'menonaktifkan' : 'mengaktifkan'
                          } kategori sertifikat`,
                        ),
                    }).then(() => {
                      invalidate({
                        resource: CERTIFICATE_CATEGORIES_RESOURCE,
                        dataProviderName: 'lms',
                        invalidates: ['list'],
                      })
                    })
                  }
                  okButtonProps={{
                    loading: isTogglingStatus,
                    danger: record.isActive,
                  }}
                  disabled={isTogglingStatus}
                >
                  <Button
                    size="small"
                    icon={
                      record.isActive ? (
                        <Icons.StopOutlined />
                      ) : (
                        <Icons.CheckCircleOutlined />
                      )
                    }
                    block
                    danger={record.isActive}
                    disabled={isTogglingStatus}
                  >
                    {record.isActive ? 'Nonaktifkan' : 'Aktifkan'}
                  </Button>
                </Popconfirm>
              </Space>
            )}
          />
        </Table>
      </List>

      <CertificateCategoryModalForm
        modalProps={createModalProps}
        formProps={createFormProps}
        onClose={createClose}
        mode="create"
      />

      <CertificateCategoryModalForm
        modalProps={editModalProps}
        formProps={editFormProps}
        initialActors={selectedEditRecord?.actors}
        onClose={() => {
          setSelectedEditRecord(null)
          editClose()
        }}
        mode="edit"
      />
    </>
  )
}
